import { handleActions } from 'redux-actions';
import qs from 'qs';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import { getFormValues } from 'redux-form';

import endpoints from '../../../config/endpoints';
import { LOCATION_CHANGE, replace } from 'react-router-redux';

import http from '../../shared/http';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';

import { regions } from '../../../../../../enums/regions';

import createFilters from '../../shared/fixed-filters';
import {default as createCalendar, getDateRangeFromCalendar} from '../../shared/calendar';
import csvExport from '../../shared/csvExport';

const calendar = createCalendar({
	namespace: 'payoutRequestRegister',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().subtract(1, 'month').format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'payoutRequestRegister',
	stateNodeName: 'filters',
	queryBuildFunc: () => '',
	listOfFilters: [
		{
			name: 'admins',
			title: 'Manager name',
			placeholder: 'Manager name',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.ADMINS,
				params: 'only_managers=true&limit=100&is_show_name=true'
			}
		},
		{
			name: 'affiliates',
			title: 'Affiliates',
			placeholder: 'Add affiliates',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.AFFILIATE_IDS
			}
		},
		{
			name: 'offers',
			title: 'Offers',
			placeholder: 'Add offers',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS
			}
		},
		{
			name: 'country_ids',
			title: 'Countries',
			placeholder: 'Add country',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.LOCATIONS_WITH_TRAFFIC,
				params: 'limit=100'
			}
		},
		{
			name: 'niches',
			title: 'Niches',
			placeholder: 'Select niches',
			value: [],
			multi: true,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'dynamic',
				source: endpoints.LOOKUPS.OFFERS_NICHES
			}
		},
		{
			name: 'regions',
			title: 'Regions',
			placeholder: 'Add regions',
			value: [],
			multi: true,
			searchable: false,
			shouldInverse: false,
			shouldShowInverseControl: false,
			options: {
				type: 'provided',
				source: regions.map(reg => ({
					value: reg,
					label: reg
				}))
			}
		}
	]
});

// action types
const FETCH_DATA_SUCCESS = 'payoutRequestRegister/fetch-data-success';
const FETCH_DATA_ERROR = 'payoutRequestRegister/fetch-data-error';

const FETCH_PAGINATED_SUCCESS = 'payoutRequestRegister/FETCH_PAGINATED_SUCCESS';
const FETCH_PAGINATED_ERROR = 'payoutRequestRegister/FETCH_PAGINATED_ERROR';

const PAGINATE = 'payoutRequestRegister/PAGINATE';

const TOGGLE_PROGRESS_BAR = 'payoutRequestRegister/toggle-progress-bar';

const APPLY = 'payoutRequestRegister/apply';

const TOGGLE_SORT = 'payoutRequests/TOGGLE_SORT';

// permissions
export const allowedPermissions = {
	payout_requests_personal_view: 'payout_requests_personal_view',
	payout_requests_view_all: 'payout_requests_view_all',
	payout_requests_view_matrix_and_history: 'payout_requests_view_matrix_and_history',
	payout_requests_review: 'payout_requests_review',
	payout_requests_manager_role: 'payout_requests_manager_role',
	payout_requests_confirm: 'payout_requests_confirm'
};

const table = {
	columns: {
		id: { index: 'id', className: 'short-id-col', title: 'ID', canBeSorted: true, isHiddenForCopy: true },
		created_at: { index: 'created_at', className: '', title: 'Created at', canBeSorted: true },
		manager: { index: 'manager', className: '', title: 'Manager', isHiddenForCopy: true },
		affiliate: { index: 'affiliate', className: '', title: 'Affiliate' },
		affiliate_id: { index: 'affiliate_id', className: '', title: 'Affiliate ID', isHiddenForCopy: true },
		offer: { index: 'offer', className: '', title: 'Offer' },
		country: { index: 'country', className: '', title: 'Country' },
		sid_campaign: { index: 'sid_campaign', className: '', title: 'Sid & Campaign ID', isHiddenForCopy: true },
		last_payout: { index: 'last_payout', className: '', title: 'Last payout', isHiddenForCopy: true },
		amount: { index: 'amount', className: '', title: 'Confirmed payout' },
		admin: { index: 'admin', className: '', title: 'Admin', isHiddenForCopy: true }
	},
	sortByColumn: {
		index: 'id',
		direction: 'desc'
	}
};
// initial state
const initialState = {
	data: null,
	systemBalances: {},
	calendar: calendar.initialState,
	table,
	showProgressBar: false,
	filters: filters.initialState,
	error: false,
	isRequestFormVisible: false,
	pagination: {
		limit: 100,
		offset: 0
	},
	sortedPayoutSystemsList: []
};

// actions
export const actions = Object.assign(
	{
		fetchData: () => (dispatch, getState) => {
			const query = {
				filters: {}
			};
			const { calendar, filters: { selectedFilters } } = getState().payoutRequestRegister;

			query.sort = selectors.getSortByColumn(getState());
			query.date_range = getDateRangeFromCalendar(calendar);
			query.limit = 100;
			query.offset = 0;

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						query.filters[filter.name] = values;
					}
				});
			}

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.REGISTER}?${qs.stringify(query)}`,
				method: 'get',
				actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
			})(dispatch);
		},

		fetchPaginatedData: () => (dispatch, getState) => {
			dispatch({ type: PAGINATE });

			const query = {
				filters: {}
			};
			const { calendar, filters: { selectedFilters } } = getState().payoutRequestRegister;

			query.sort = selectors.getSortByColumn(getState());
			query.date_range = getDateRangeFromCalendar(calendar);
			const pagination = selectors.getPagination(getState());

			if (selectedFilters) {
				selectedFilters.forEach(filter => {
					const values = filter.value.map(f => f.value);
					if (values.length) {
						query.filters[filter.name] = values;
					}
				});
			}

			return http({
				url: `${endpoints.PAYOUT_REQUESTS.REGISTER}?${qs.stringify({...query, ...pagination })}`,
				method: 'get',
				actions: [FETCH_PAGINATED_SUCCESS, FETCH_PAGINATED_ERROR]
			})(dispatch);
		},

		download: () => (dispatch, getState) => {
			const { rows } = selectors.getTable(getState());
			const headers = [
				'manager', 'affiliate', 'affiliate_id', 'offer', 'country', 'sid',
				'campaign_id', 'payout_decision', 'currency', 'status', 'amount', 'current_payout'
			];
			const headersMapping = {
				amount: 'requested_amount'
			};

			const columns = rows.map(entity => headers.map(h => entity[h] || '-'));

			const rowsForExport = [].concat([headers.map(h => headersMapping[h] || h)], columns);

			csvExport('Payout_requests_register', rowsForExport);
		},

		apply: () => dispatch => {
			dispatch(actions.fetchData());
		},

		toggleSort: (col) => dispatch => {
			if (!col) {
				return;
			}
			dispatch({ type: TOGGLE_SORT, payload: col });
			dispatch(actions.fetchData());
		},

		clear: () => (dispatch, getState) => {
			dispatch(replace('/payouts/'));
		},
		toggleProgressBar: () => ({
			type: TOGGLE_PROGRESS_BAR
		})
	},
	calendar.actions,
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			return {
				...state,
				data: action.payload.data,
				showProgressBar: false,
				error: false
			};
		},
		[PAGINATE]: (state, action) => {
			return {
				...state,
				pagination: {
					...state.pagination,
					offset: state.pagination.offset + state.pagination.limit
				}
			};
		},
		[FETCH_PAGINATED_SUCCESS]: (state, action) => {
			return {
				...state,
				data: [].concat(action.payload.data || []).concat(state.data || []),
				showProgressBar: false,
				error: false
			};
		},
		[APPLY]: (state, action) => {
			return {
				...state,
				selectedFilters: {
					...action.payload
				}
			};
		},
		[TOGGLE_SORT]: (state, action) => ({
			...state,
			table: {
				...state.table,
				sortByColumn: {
					...state.table.sortByColumn,
					index: action.payload,
					direction: action.payload === state.table.sortByColumn.index &&
					state.table.sortByColumn.direction === 'asc' ? 'desc' : 'asc'
				}
			}
		}),
		[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
		[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar })
	},
	calendar.reducers,
	filters.reducers
);

// reducers
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.payoutRequestRegister.data,
	getIsHiddenCopyCols: state => {
		return getFormValues('createPayoutRequestToggleCopyForm')(state)
			&& getFormValues('createPayoutRequestToggleCopyForm')(state).is_hidden_copy;
	},
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.payoutRequestRegister.calendar, state => state
	)(state),
	getProgressBar: state => state.payoutRequestRegister.showProgressBar,
	getTable: state => {
		const { table, data } = state.payoutRequestRegister;
		const rows = (data || []).map(item => ({
			...item, created_at: moment(item.created_at).format('DD-MM-YYYY HH:mm:ss')
		}));
		return {
			headers: Object.values(table.columns),
			rows
		};
	},
	getSortByColumn: state => state.payoutRequestRegister.table.sortByColumn,
	getPagination: state => state.payoutRequestRegister.pagination,
	getIsRequestFormVisible: state => state.payoutRequestRegister.isRequestFormVisible,
	getDeniedToCopyWords: state => {
		const { rows } = selectors.getTable(state);

		return []
			.concat(rows.map(item => item.affiliate))
			.concat(rows.map(item => item.created_at));
	},
	getIntl: state => intl(state),
	getCan: state => checkPermissions(state, allowedPermissions),
	getFilters: state => filters.selectors.getFilters(
		state => state.payoutRequestRegister.filters,
		state => state
	)(state),
	getRequestFormInitialValues: state => ({
		currency: 'usd'
	}),
	getError: state => state.payoutRequestRegister.error
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/payout-requests/list-register/index.js